/* eslint-disable react-hooks/exhaustive-deps */
import {
    AppstoreFilled,
    ClockCircleOutlined,
    FilterFilled,
    FilterOutlined,
    HeartFilled,
    RightOutlined,
} from '@ant-design/icons';
import { Badge, Button, Collapse, Divider, Empty, Space, Spin, Tag, Typography } from 'antd';
import { differenceBy } from 'lodash';
import { Activity, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import Categories from '../../components/ui/Categories';
import CustomerSearch from '../../components/ui/CustomerSearch';
import LoadingCard from '../../components/ui/LoadingCard';
import ProductCard from '../../components/ui/ProductCard';
import SearchInput from '../../components/ui/SearchInput';
import { addOrder, addOrderItem } from '../../redux/actions/order';
import {
    addFavourite,
    getProductsWithPagination,
    removeFavourite,
} from '../../redux/actions/products';
import {
    clearFilters,
    removeCategory,
    setCustomer,
    setFavourite,
    setRecent,
    setSearch,
} from '../../redux/slices/filtersSlice';
import { setOpenOrderId, setOrderItems } from '../../redux/slices/orderSlice';
import { store } from '../../redux/store';
import { errorToast } from '../../utils/toast';
import FilterView from './Filter';
import { getRoutes, ROLES } from '../../routes/Routes';

const { Title, Text } = Typography;

const Catalogue = ({ isDrawer = false }) => {
    const { user } = useSelector((state) => state.authSlice);
    const { search, category, customer, favourite, recent, offer } = useSelector(
        (s) => s.filtersSlice
    );
    const { products, pagination, openOrderItems } = useSelector((state) => state.productsSlice);
    const { openOrderId } = useSelector((state) => state.orderSlice);

    const [categoryOpen, setCategoryOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [scrollParent, setScrollParent] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const hasFetched = useRef(false);
    const abortControllerRef = useRef(null);

    const hasMore = pagination.page < pagination.totalPages;
    const hasActiveFilters = [category?.length, search, customer, favourite, offer].some(Boolean);

    const fetchProducts = useEffectEvent(async (params = {}, append = false, showLoading = true) => {
        if (abortControllerRef.current) abortControllerRef.current.abort();

        const controller = new AbortController();
        abortControllerRef.current = controller;


        if (showLoading) setLoading(true);
        try {
            await getProductsWithPagination({
                body: {
                    searchText: params.searchText ?? store.getState().filtersSlice.search,
                    customer_id: params.customer_id ?? store.getState().filtersSlice.customer,
                    category: params.category ?? store.getState().filtersSlice.category,
                    favourite: params.favourite ?? store.getState().filtersSlice.favourite,
                    offer: params.offer ?? store.getState().filtersSlice.offer,
                    recent: params.recent ?? store.getState().filtersSlice.recent,
                    page: params.page ?? 1,
                },
                isAppend: append,
                signal: controller.signal,
            });
        } catch (err) {
            if (err.code === 'ERR_CANCELED') console.log('Previous request canceled');
            else throw err;
        } finally {
            if (showLoading) setLoading(false);
        }
    });

    const fetchProductsDebounced = useDebouncedCallback((params = {}) => {
        fetchProducts(params);
    }, 400);

    const handleSearchChange = (value) => {
        store.dispatch(setSearch(value));
        fetchProductsDebounced({ searchText: value });
    };

    const handleCustomerChange = (value) => {
        store.dispatch(setCustomer(value));
        fetchProducts({ customer_id: value || '' });
    };

    const handleApplyCategory = () => {
        setCategoryOpen(false);
        fetchProducts();
    };

    const handleToggleFavorites = () => {
        const next = favourite === 0 ? 1 : 0;
        store.dispatch(setFavourite(next));
        fetchProducts({ favourite: next });
    };

    // const handleToggleOffer = () => {
    //     const next = offer === 0 ? 1 : 0;
    //     store.dispatch(setOffer(next));
    //     fetchProducts({ offer: next });
    // };

    const handleToggleRecent = () => {
        const next = recent === 0 ? 1 : 0;
        store.dispatch(setRecent(next));
        fetchProducts({ recent: next });
    };

    const handleRemoveCategory = (filter) => {
        const updated = category.filter((c) => c !== filter);
        store.dispatch(removeCategory(filter));
        fetchProducts({ category: updated });
    };

    const handleToggleFav = useEffectEvent((product) => {
        const isFavourite = !!product?.favourite;
        const filters = { searchText: search, customer_id: customer, category, favourite, recent };
        isFavourite
            ? removeFavourite({ product_netsuite_id: product?.netsuite_id }, filters)
            : addFavourite({ product_netsuite_id: product?.netsuite_id }, filters);
    });

    const getMoreProducts = useEffectEvent(async () => {
        if (!hasMore || loadingMore) return;
        setLoadingMore(true);
        try {
            await fetchProducts(
                {
                    searchText: search,
                    customer_id: customer,
                    category,
                    favourite,
                    recent,
                    offer,
                    page: pagination.page + 1,
                },
                true,
                false
            );
        } finally {
            setLoadingMore(false);
        }
    });

    const handleClearAll = () => {
        store.dispatch(clearFilters());
        fetchProducts({
            searchText: '',
            customer_id: null,
            favourite: 0,
            category: [],
            offer: 0,
            recent: 0,
        });
        setFilterOpen(false);
    };

    const handleCreateOrder = useEffectEvent(async (payload, product) => {
        if (!customer) return errorToast('Please select a customer to create an order.');

        const res = await addOrder({ customer_netsuite_id: customer });
        if (res?.ack) {
            store.dispatch(setOpenOrderId(res?.record_id));
            if (res?.record_id) payload.order_id = res?.record_id;
            addOrderItem(payload, product);
        }
    });

    const filterOpenProducts = useMemo(() => {
        const items = differenceBy(products, openOrderItems, 'netsuite_id');
        if (loadingMore) {
            return [
                ...items,
                ...Array.from({ length: 1 }).map((_, i) => ({ type: 'loading', id: `loading-${i}` })),
            ];
        }
        return items;
    }, [products, openOrderItems, loadingMore]);

    const itemContent = useCallback(
        (index, product) => {
            if (product?.type === 'loading') {
                return (
                    <div className="product-grid-item" key={product.id}>
                        <LoadingCard />
                    </div>
                );
            }
            return (
                <div className="product-grid-item" key={product?.netsuite_id || index}>
                    <ProductCard
                        product={product}
                        onFavToggle={handleToggleFav}
                        onCreateOrder={handleCreateOrder}
                        showFav
                        openOrderId={openOrderId}
                        isPriceLevels={user.role !== ROLES.CUSTOMER}
                    />
                </div>
            );
        },
        [handleToggleFav, handleCreateOrder, openOrderId]
    );

    useEffect(() => {
        if (!isDrawer) {
            store.dispatch(clearFilters());
            store.dispatch(setOrderItems([]));
        }

        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchProducts();
        }
    }, []);

    return (
        <div className="catalogue-page" ref={setScrollParent}>
            {!isDrawer && (
                <div className="flex justify-content-between align-items-center gap-2">
                    <div className="mb-3">
                        <Title level={3} className="page-title">
                            Product Catalogue
                        </Title>
                        <Text type="secondary">Browse and manage your product inventory</Text>
                    </div>
                </div>
            )}

            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex align-items-center flex-wrap gap-2 flex-1">
                    <SearchInput
                        placeholder="Search products by name or code"
                        onSearch={handleSearchChange}
                        maxWidth="25rem"
                    />
                    <div className="max-w-25rem w-full lg-filter">
                        <CustomerSearch
                            size="large"
                            placeholder="Search customer..."
                            showUnassigned={false}
                            onChange={handleCustomerChange}
                            value={customer}
                            disabled={isDrawer}
                        />
                    </div>
                </div>

                <Space>
                    {hasActiveFilters && !isDrawer && (
                        <Button variant="link" color="danger" onClick={handleClearAll} className="lg-filter">
                            Clear All
                        </Button>
                    )}

                    <Button
                        className="lg-filter"
                        size="large"
                        variant={favourite ? 'solid' : 'filled'}
                        color="pink"
                        icon={<HeartFilled />}
                        onClick={handleToggleFavorites}
                    >
                        Favourites
                    </Button>
                    <Activity mode={!!customer ? 'visible' : 'hidden'}>
                        <Button
                            className="lg-filter"
                            size="large"
                            variant={recent ? 'solid' : 'filled'}
                            color="blue"
                            icon={<ClockCircleOutlined />}
                            onClick={handleToggleRecent}
                        >
                            Recent Items
                        </Button>
                        {/* <Button
                            size="large"
                            variant={offer ? 'solid' : 'filled'}
                            color="red"
                            icon={<ThunderboltOutlined />}
                            onClick={handleToggleOffer}
                        >
                            Offers
                        </Button> */}
                    </Activity>
                    <Button
                        className="lg-filter"
                        size="large"
                        variant="filled"
                        color="default"
                        icon={<AppstoreFilled />}
                        onClick={() => setCategoryOpen(true)}
                    >
                        Categories {!!category?.length && `(${category.length})`} <RightOutlined />
                    </Button>
                    <Button
                        className="md:block lg:hidden"
                        icon={
                            hasActiveFilters ? (
                                <Badge dot>
                                    <FilterFilled className="text-lg" />
                                </Badge>
                            ) : (
                                <FilterOutlined className="text-lg" />
                            )
                        }
                        size="large"
                        variant="filled"
                        color="default"
                        onClick={() => setFilterOpen(true)}
                    />
                </Space>
            </div>

            {category?.length > 0 && (
                <div className="flex align-items-center mb-3 gap-2 overflow-x-auto no-scrollbar py-1">
                    <Text className="flex-shrink-0">Active Filters:</Text>
                    <div className="flex gap-2 flex-nowrap">
                        {category.map((filter, i) => (
                            <Tag
                                color="#f5f5f5"
                                className="filter-tag shadow-1 whitespace-nowrap"
                                key={i}
                                closable
                                onClose={() => handleRemoveCategory(filter)}
                            >
                                {filter}
                            </Tag>
                        ))}
                    </div>
                </div>
            )}

            <Divider className="my-2" />

            <div className="catalogue">
                {!!openOrderItems?.length && (
                    <Collapse
                        className="added-products-section mb-2 "
                        size="small"
                        items={[
                            {
                                key: '1',
                                label: (
                                    <div className="flex align-items-center gap-2">
                                        <Typography.Text strong>On Order</Typography.Text>
                                        <Badge count={openOrderItems?.length} />
                                    </div>
                                ),
                                extra: (
                                    <Button
                                        size="small"
                                        variant="link"
                                        color="primary"
                                        href={getRoutes().order_details.route(openOrderId)}
                                    >
                                        Go to Order
                                    </Button>
                                ),
                                children: (
                                    <div className="product-grid">
                                        {openOrderItems.map((product, i) => (
                                            <div key={product?.netsuite_id || i}>
                                                <ProductCard
                                                    product={product}
                                                    onFavToggle={handleToggleFav}
                                                    onCreateOrder={handleCreateOrder}
                                                    showFav={false}
                                                    openOrderId={openOrderId}
                                                    isRemoveProduct
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}

                {loading ? (
                    <div className="flex justify-content-center align-items-center gap-3 py-8">
                        <Spin />
                        <Text type="secondary" className="text-lg">
                            Loading products, please wait...
                        </Text>
                    </div>
                ) : !!filterOpenProducts?.length ? (
                    scrollParent && (
                        <VirtuosoGrid
                            useWindowScroll={false}
                            customScrollParent={scrollParent}
                            data={filterOpenProducts}
                            endReached={getMoreProducts}
                            overscan={200}
                            listClassName="product-grid"
                            computeItemKey={(index, item) => item?.netsuite_id || index}
                            itemContent={itemContent}
                        />
                    )
                ) : (
                    <div className="flex justify-content-center align-items-center">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<Text type="secondary">No products found</Text>}
                        />
                    </div>
                )}
            </div>

            {categoryOpen && (
                <Categories
                    open={categoryOpen}
                    onClose={() => setCategoryOpen(false)}
                    onApply={handleApplyCategory}
                />
            )}

            <Activity mode={filterOpen ? 'visible' : 'hidden'}>
                <FilterView
                    isCustomerDisable={isDrawer}
                    open={filterOpen}
                    onClear={handleClearAll}
                    onClose={() => setFilterOpen(false)}
                    onCustomerChange={handleCustomerChange}
                    onToggleFavorites={handleToggleFavorites}
                    onToggleRecent={handleToggleRecent}
                    isClearable={hasActiveFilters}
                    onToggleCategory={() => setCategoryOpen(true)}
                />
            </Activity>
        </div>
    );
};

export default Catalogue;
